export const KF_KEYS = ["x", "y", "scale", "rot", "opacity"];
const CURVED = ["x", "y"];
const TOL = 0.02;
export const EASE = { linear: (s) => s, in: (s) => s * s, out: (s) => s * (2 - s), inout: (s) => s * s * (3 - 2 * s) };

const pick = (o) => Object.fromEntries(KF_KEYS.map((k) => [k, o[k]]));
const sortKfs = (kfs) => [...kfs].sort((a, b) => a.t - b.t);
const round = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, typeof v === "number" ? +v.toFixed(4) : v]));
export const catmull = (p0, p1, p2, p3, s) =>
  0.5 * (2 * p1 + (-p0 + p2) * s + (2 * p0 - 5 * p1 + 4 * p2 - p3) * s * s + (-p0 + 3 * p1 - 3 * p2 + p3) * s * s * s);

export function sample(kfs, t) {
  const n = kfs.length;
  if (n === 1 || t <= kfs[0].t) return pick(kfs[0]);
  if (t >= kfs[n - 1].t) return pick(kfs[n - 1]);
  let i = 0;
  while (t > kfs[i + 1].t) i++;
  const s = EASE[kfs[i].ease || "linear"]((t - kfs[i].t) / Math.max(1e-6, kfs[i + 1].t - kfs[i].t));
  const P = (j) => kfs[Math.max(0, Math.min(n - 1, j))];
  return Object.fromEntries(KF_KEYS.map((k) => [k, CURVED.includes(k)
    ? catmull(P(i - 1)[k], P(i)[k], P(i + 1)[k], P(i + 2)[k], s)
    : P(i)[k] + (P(i + 1)[k] - P(i)[k]) * s]));
}

export const migrateFx = (fx, len) => (fx.kfs || !fx.anim?.on ? fx
  : { ...fx, kfs: [{ t: 0, ...pick(fx) }, { t: +len.toFixed(3), ...pick({ ...fx, ...fx.anim }) }] });
export const hasKfs = (fx) => (fx.kfs || []).length > 0;
export const kfIndexAt = (fx, lt) => (fx.kfs || []).findIndex((k) => Math.abs(k.t - lt) < TOL);
export const layerAt = (fx, lt) => (hasKfs(fx) ? { ...fx, ...sample(fx.kfs, lt) } : fx);

export function kfSet(fx, lt, patch) {
  if (!hasKfs(fx)) return patch;
  const i = kfIndexAt(fx, lt);
  if (i >= 0) return { kfs: fx.kfs.map((k, j) => (j === i ? round({ ...k, ...patch }) : k)) };
  return { kfs: sortKfs([...fx.kfs, round({ ...sample(fx.kfs, lt), ...patch, t: lt })]) };
}
export const addKf = (fx, lt) => ({ kfs: sortKfs([...(fx.kfs || []).filter((k) => Math.abs(k.t - lt) >= TOL), round({ ...pick(layerAt(fx, lt)), t: lt })]) });
export function delKf(fx, i) {
  const v = pick(layerAt(fx, fx.kfs[i].t));
  const kfs = fx.kfs.filter((_, j) => j !== i);
  return kfs.length ? { kfs } : { kfs: [], ...v };
}

export function pathPoints(fx, len, steps = 60) {
  if ((fx.kfs || []).length < 2) return [];
  const a = fx.kfs[0].t, b = Math.min(len, fx.kfs[fx.kfs.length - 1].t);
  return Array.from({ length: steps + 1 }, (_, i) => sample(fx.kfs, a + ((b - a) * i) / steps));
}

export const shiftKfs = (fx, dt) => (hasKfs(fx) ? { kfs: fx.kfs.map((k) => ({ ...k, t: +(k.t + dt).toFixed(3) })) } : {});
export function sliceKfs(fx, from, to) {
  if (!hasKfs(fx)) return {};
  const edge = (t) => round({ ...sample(fx.kfs, t), t: t - from });
  const mid = fx.kfs.filter((k) => k.t > from + TOL && k.t < to - TOL).map((k) => ({ ...k, t: +(k.t - from).toFixed(3) }));
  return { kfs: [edge(from), ...mid, edge(to)] };
}
